// JS twin of backend/cover_framing.py — must stay in sync (tests cover both against the same cases).
const FIT_TOLERANCE = 0.05;
const EDGE_MARGIN = 0.015;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const normBox = (b) => {
  if (!Array.isArray(b) || b.length !== 4) return null;
  let [x0, y0, x1, y1] = b.map(Number);
  if ([x0, y0, x1, y1].some((v) => Number.isNaN(v))) return null;
  if (x1 < x0) [x0, x1] = [x1, x0];
  if (y1 < y0) [y0, y1] = [y1, y0];
  [x0, y0, x1, y1] = [x0, y0, x1, y1].map((v) => clamp(v, 0, 1));
  if (x1 - x0 < 0.02 || y1 - y0 < 0.02) return null;
  return [x0, y0, x1, y1];
};

const positionFor = (lo, hi, vis) => {
  if (vis >= 1) return 50;
  const centre = (lo + hi) / 2;
  const start = clamp(centre - vis / 2, 0, 1 - vis);
  return Math.round((start / (1 - vis)) * 1000) / 10;
};

export const DEFAULT_FRAMING = { mode: "cover", x: 50, y: 50, zoom: 1, source: "default", reason: "no subject analysis yet" };

const anchorWindow = (lo, hi, aLo, aHi, vis) => {
  if (vis >= 1) return 50;
  if (aLo == null) { aHi = hi; aLo = hi - Math.min(vis, hi - lo); }
  const end = clamp(Math.max(aHi, aLo + 0.01), vis, 1);
  const start = clamp(end - vis, 0, 1 - vis);
  return Math.round((start / (1 - vis)) * 1000) / 10;
};

// ALWAYS a filled object-fit: cover crop — only the focal point (x/y) and zoom are chosen.
export function computeFraming(raw, cardAspect = 1) {
  raw = raw || {};
  const manual = raw.manual && typeof raw.manual === "object" ? raw.manual : null;
  if (manual && ("x" in manual || "y" in manual || "zoom" in manual)) {
    return { mode: "cover", x: clamp(Number(manual.x ?? 50), 0, 100), y: clamp(Number(manual.y ?? 50), 0, 100), zoom: clamp(Number(manual.zoom ?? 1), 1, 1.6), source: "manual", reason: "admin override" };
  }
  const ar = Number(raw.ar) || 0;
  let box = normBox(raw.box);
  const feet = normBox(raw.feet);
  if (ar <= 0 || (!box && !feet)) return DEFAULT_FRAMING;
  if (!box) box = feet;
  const x0 = clamp(box[0] - EDGE_MARGIN, 0, 1), y0 = clamp(box[1] - EDGE_MARGIN, 0, 1);
  const x1 = clamp(box[2] + EDGE_MARGIN, 0, 1), y1 = clamp(box[3] + EDGE_MARGIN, 0, 1);
  if (ar >= cardAspect) {
    const vis = cardAspect / ar, extent = x1 - x0;
    if (extent <= vis + FIT_TOLERANCE) return { mode: "cover", x: positionFor(x0, x1, vis), y: 50, zoom: 1, source: "smart", reason: "whole subject fits horizontally" };
    const fx0 = feet ? feet[0] : (x0 + x1) / 2 - 0.05, fx1 = feet ? feet[2] : (x0 + x1) / 2 + 0.05;
    return { mode: "cover", x: positionFor(fx0, fx1, vis), y: 50, zoom: 1, source: "smart", reason: "subject wider than window — centred on the feet" };
  }
  const vis = ar / cardAspect, extent = y1 - y0;
  if (extent <= vis + FIT_TOLERANCE) return { mode: "cover", x: 50, y: positionFor(y0, y1, vis), zoom: 1, source: "smart", reason: "whole subject fits vertically" };
  const y = feet ? anchorWindow(y0, y1, feet[1], clamp(feet[3] + EDGE_MARGIN, 0, 1), vis) : anchorWindow(y0, y1, null, null, vis);
  return { mode: "cover", x: 50, y, zoom: 1, source: "smart", reason: "subject taller than window — feet kept, cropped from the top" };
}
